import React, { useEffect, useState } from "react"
import { Button, Card, Input, Typography, Avatar, Spin, Divider } from "antd"
import { SendOutlined, UserOutlined, ArrowLeftOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import PageLayout from "./PageLayout"
import { submitFeedback, getFeedbacks, Feedback } from "./endpoints"

const { Title, Text } = Typography

export default function FeedbackPage() {
  const navigate = useNavigate()
  const [name, setName] = useState("")
  const [comment, setComment] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  const loadFeedbacks = () => {
    setLoadingList(true)
    getFeedbacks()
      .then(setFeedbacks)
      .catch((e) => setListError(e?.message ?? "Failed to load feedback."))
      .finally(() => setLoadingList(false))
  }

  useEffect(() => { loadFeedbacks() }, [])

  const handleSubmit = async () => {
    if (!comment.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await submitFeedback(name.trim() || "Anonymous", comment.trim())
      setSubmitted(true)
      setName("")
      setComment("")
      loadFeedbacks()
    } catch (e: any) {
      setError(e?.message ?? "Failed to submit. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PageLayout>
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <Button
          icon={<ArrowLeftOutlined />}
          type="link"
          onClick={() => navigate("/")}
          style={{ padding: 0, marginBottom: 16, color: "#1677ff" }}
        >
          Back to editor
        </Button>

        {/* ── Leave Feedback form ── */}
        <Card style={{ borderRadius: 12, border: "1px solid #e8e8e8", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: 20 }}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
              <Title level={5} style={{ margin: "0 0 4px" }}>Thank you for your feedback!</Title>
              <Text type="secondary">It has been posted below.</Text>
              <div style={{ marginTop: 16 }}>
                <Button onClick={() => setSubmitted(false)}>Leave another comment</Button>
              </div>
            </div>
          ) : (
            <>
              <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>Leave Feedback</Title>
              <Text type="secondary" style={{ display: "block", marginBottom: 20, fontSize: 13 }}>
                Let me know what you think — your feedback helps improve the tool.
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Input
                  prefix={<UserOutlined style={{ color: "#bbb" }} />}
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Input.TextArea
                  placeholder="How was your experience? Any suggestions?"
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  showCount
                  maxLength={1000}
                />
                {error && <Text type="danger">{error}</Text>}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={submitting}
                    disabled={!comment.trim()}
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* ── Community Feedback list ── */}
        <Card style={{ borderRadius: 12, border: "1px solid #e8e8e8", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
            <Title level={5} style={{ margin: 0 }}>Community Feedback</Title>
            {feedbacks.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {feedbacks.length} response{feedbacks.length !== 1 ? "s" : ""}
              </Text>
            )}
          </div>
          <Divider style={{ margin: "10px 0 14px" }} />
          {loadingList ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}><Spin /></div>
          ) : listError ? (
            <Text type="danger">{listError}</Text>
          ) : feedbacks.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>No feedback yet — be the first!</div>
          ) : (
            feedbacks.map((item, idx) => (
              <div
                key={item.id}
                style={{
                  display: "flex", gap: 12, padding: "12px 0",
                  borderBottom: idx < feedbacks.length - 1 ? "1px solid #f0f0f0" : "none",
                  alignItems: "flex-start",
                }}
              >
                <Avatar icon={<UserOutlined />} style={{ background: "#1677ff", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{item.user}</span>
                    <Text type="secondary" style={{ fontSize: 11 }}>{new Date(item.ts).toLocaleString()}</Text>
                  </div>
                  <p style={{ margin: 0, whiteSpace: "pre-wrap", color: "#333", fontSize: 14 }}>{item.comments}</p>
                </div>
              </div>
            ))
          )}
        </Card>
      </div>
    </PageLayout>
  )
}
